<template>
    <div>
        <b-modal v-if="!embed" ref="taskModal" :title="getTitle" size="xl" no-fade ok-title="Save Task"
            @hidden="onModalHidden" @ok="handleSaveTask" scrollable body-class="p-0">
            <div v-if="loading" class="text-center my-2">
                <b-spinner class="align-middle mr-2" />
                <strong>Loading...</strong>
            </div>
            <b-overlay v-else :show="processingPreSave">
                <TaskEditor ref="taskEditor" :key="activeTask.id"
                    :show-completion-question-errors="showCompletionQuestionErrors"
                    :fieldsDisabled="shouldDisableFields" v-model="activeTask" />
            </b-overlay>
            <template #modal-header="{ close }">
                <div class="w-100 d-flex flex-row align-items-baseline">
                    <h5 class="modal-title">{{ getTitle }}</h5>
                    <div class="ml-auto d-flex flex-row align-items-end">
                        <b-button v-if="showWatching" disabled class="m-0" variant="outline-secondary" size="sm">
                            <i class="fas fa-eye" />
                            Watching
                        </b-button>
                        <b-button v-else-if="showWatchAction" variant="outline-primary" size="sm" class="m-0"
                            @click="addMeAsWatcher">
                            <i class="fa fa-eye" />
                            Watch
                        </b-button>
                        <history-modal v-if="template && activeTask?.id" v-model="activeTask" class="mx-4" />
                        <button type="button" aria-label="Close" class="close" @click="close">×</button>
                    </div>
                </div>
            </template>
            <template #modal-footer="{ cancel, ok }">
                <div v-if="!loading" class="d-flex w-100 mt-2" style="margin-bottom: -5px;">
                    <div class="flex-fill d-flex align-items-center" style="gap: 13px;">
                        <b-button @click="cancel" class="mb-0">Cancel</b-button>

                        <b-dropdown v-if="(activeTask.id && !activeTask.isScheduled && !activeTask.isTemplate) || showMergeAnswersAction"
                            text="Actions" variant="secondary" class="mb-0" dropup>
                            <b-dropdown-item v-if="activeTask.id && !activeTask.isScheduled && !activeTask.isTemplate"
                                @click="showFaxModal">
                                <i class="fa fa-print fa-fw" /> Fax...
                            </b-dropdown-item>
                            <b-dropdown-item v-if="activeTask.id && !activeTask.isScheduled && !activeTask.isTemplate"
                                @click="showTextModal">
                                <i class="fa fa-comment fa-fw" /> Send Text...
                            </b-dropdown-item>
                            <b-dropdown-item v-if="showMergeAnswersAction"
                                @click="showMergeModal">
                                <i class="fa fa-file-pdf-o fa-fw" /> Merge Answers...
                            </b-dropdown-item>
                        </b-dropdown>

                        <div v-if="activeTask.id" style="line-height: 1.1;">
                            <small>
                                <strong>Created:</strong><br>
                                {{ activeTask.createdBy }} on
                                {{ activeTask.created | formatDate("L") }}
                            </small>
                        </div>
                    </div>
                    <div class="d-flex align-items-center" style="gap: 9px;">
                        <b-button v-if="showCancelTaskAction" variant="outline-danger" @click="openCancelTaskModal" class="mb-0">
                            Cancel Task
                        </b-button>
                        <b-button v-if="showReopenTaskAction" variant="primary" @click="openReopenTaskModal" class="mb-0">
                            Reopen Task
                        </b-button>
                        <b-button v-if="showCompleteAction" variant="success" @click="completeActiveTask" class="mb-0">
                            Complete Task
                        </b-button>
                        <b-button v-if="showSaveButton"
                            variant="primary" @click="ok" class="mb-0">
                            {{ activeTask.id ? "Save " : "Create " }}
                            {{ activeTask?.isScheduled ? "Scheduled Task" : activeTask?.isTemplate ? "Template" : "Task"
                            }}
                        </b-button>
                    </div>
                </div>
                <div v-else></div>
            </template>

        </b-modal>
        <div v-else>
            <div v-if="loading" class="text-center my-2">
                <b-spinner class="align-middle mr-2" />
                <strong>Loading...</strong>
            </div>
            <template v-else>
                <h1 class="card-title">
                    {{ activeTask?.isScheduled ? "Scheduled Task" : activeTask?.isTemplate ? "Task Template" : "Task" }}
                </h1>

                <TaskEditor ref="taskEditor" :key="activeTask.id"
                    :show-completion-question-errors="showCompletionQuestionErrors"
                    :fieldsDisabled="shouldDisableFields" v-model="activeTask" />

                <div class="actions glass-pane d-flex align-items-center" style="gap: 7px;">
                    <div class="d-flex flex-fill align-items-center" style="gap: 10px;">
                        <b-dropdown v-if="(activeTask.id && !activeTask.isScheduled && !activeTask.isTemplate) || showMergeAnswersAction"
                            text="Actions" variant="secondary" class="mb-0" dropup>
                            <b-dropdown-item v-if="activeTask.id && !activeTask.isScheduled && !activeTask.isTemplate"
                                @click="showFaxModal">
                                <i class="fa fa-print fa-fw" /> Fax...
                            </b-dropdown-item>
                            <b-dropdown-item v-if="activeTask.id && !activeTask.isScheduled && !activeTask.isTemplate"
                                @click="showTextModal">
                                <i class="fa fa-comment fa-fw" /> Send Text...
                            </b-dropdown-item>
                            <b-dropdown-item v-if="showMergeAnswersAction"
                                @click="showMergeModal">
                                <i class="fa fa-file-pdf-o fa-fw" /> Merge Answers...
                            </b-dropdown-item>
                        </b-dropdown>
                        <div v-if="activeTask.id" style="line-height: 1.1;">
                            <small>
                                <strong>Created:</strong><br>
                                {{ activeTask.createdBy }} on
                                {{ activeTask.created | formatDate("L") }}
                            </small>
                        </div>
                    </div>
                    <div>
                        <b-button v-if="showCancelTaskAction" variant="outline-danger" @click="openCancelTaskModal" class="mb-0">
                            Cancel Task
                        </b-button>
                        <b-button v-if="showReopenTaskAction" variant="primary" @click="openReopenTaskModal" class="mb-0">
                            Reopen Task
                        </b-button>
                        <b-button v-if="showCompleteAction" variant="success" @click="completeActiveTask" class="mb-0">
                            Complete Task
                        </b-button>
                        <b-button v-if="showSaveButton"
                            variant="primary" @click="handleSaveTask" class="mb-0">
                            {{ activeTask.id ? "Save " : "Create " }}
                            {{ scheduled || activeTask.isScheduled ? "Scheduled " : "" }}
                            Task
                        </b-button>
                    </div>

                </div>
            </template>
        </div>

        <!-- SD-3303: Cancel Task modal -->
        <b-modal id="task-cancel-modal" ref="cancelTaskModal" title="Cancel Task"
                 ok-title="Cancel Task" ok-variant="danger" cancel-title="Keep Task"
                 :ok-disabled="cancelTaskReasonRequired && !cancelTaskReason.trim()"
                 @ok="confirmCancelTask" @hidden="cancelTaskReason = ''">
            <p>This will mark the task as <strong>Cancelled</strong>. Any partial question answers are preserved for audit. The task can be reopened later.</p>
            <b-form-group :label="cancelTaskReasonRequired ? 'Reason (required):' : 'Reason (optional):'"
                          :description="`${cancelTaskReason.length}/4000 characters`">
                <b-textarea v-model="cancelTaskReason" maxlength="4000" rows="3" />
            </b-form-group>
        </b-modal>

        <!-- SD-3303: Reopen Task modal -->
        <b-modal id="task-reopen-modal" ref="reopenTaskModal" title="Reopen Cancelled Task"
                 ok-title="Reopen" ok-variant="primary" @ok="confirmReopenTask">
            <b-form-group label="Reopen as:">
                <b-form-radio-group v-model="reopenTaskTargetStatus"
                                    :options="[
                                        { text: 'New', value: 0 },
                                        { text: 'In Progress', value: 1 },
                                    ]" />
            </b-form-group>
        </b-modal>

        <b-modal ref="closureQuestionsModal" title="Task Completion" size="lg" no-fade ok-variant="success"
            ok-title="Complete Task" scrollable @ok="handleCompleteQuestionsSubmit"
            :ok-disabled="loadingCompletionForm" modal-class="closure-questions-modal">
            <template #modal-footer="{ ok, cancel }">
                <div class="d-flex w-100" style="gap: 8px;">
                    <b-button @click="cancel" variant="secondary" :disabled="savingPartialAnswers || loadingCompletionForm">
                        Cancel
                    </b-button>
                    <div class="flex-fill"></div>
                    <b-button @click="handleSavePartialAnswers" variant="info" :disabled="savingPartialAnswers || loadingCompletionForm">
                        <b-spinner v-if="savingPartialAnswers" small class="mr-1" />
                        {{ savingPartialAnswers ? 'Saving...' : 'Save & Continue Later' }}
                    </b-button>
                    <b-button @click="ok" variant="success" :disabled="savingPartialAnswers || loadingCompletionForm">
                        Complete Task
                    </b-button>
                </div>
            </template>
            <TaskQuestions v-show="activeTask.completionQuestionSet?.questions?.length" ref="closureQuestions"
                :questions="activeTask.completionQuestionSet?.questions" :answers="activeClosureAnswers"
                :show-errors="showCompletionQuestionAnswerErrors" @question-input="processQuestionAnswer" />

            <hr v-if="activeTask.completionQuestionSet?.questions?.length && activeTask.completionFormImageId">

            <div v-if="loadingCompletionForm">
                <b-spinner small /> Generating Form...
            </div>
            <div v-else-if="activeTask.completionFormImageId" style="position: relative;">
                <ImageComponent :key="activeTask.completionFormImageId" style="height:calc(100vh - 300px);"
                    :imageID="activeTask.completionFormImageId" />

                <div class="text-center w-100" style="position: absolute; bottom: 0;">
                    <div class="glass-pane d-inline-block rounded m-3 px-3">
                        <div class="d-flex" style="gap: 7px;">
                            <b-button
                                :variant="activeTask.completionFormImageAccepted === true ? 'success' : 'outline-success'"
                                @click="activeTask.completionFormImageAccepted = true">
                                Accept
                            </b-button>
                            <b-button
                                :variant="activeTask.completionFormImageAccepted === false ? 'danger' : 'outline-danger'"
                                @click="activeTask.completionFormImageAccepted = false">
                                Reject
                            </b-button>
                        </div>
                    </div>
                </div>
            </div>
        </b-modal>

        <b-modal ref="faxModal" :title="isContactTemplateSelected ? 'Send Fax' : 'Fax Task Summary'" no-fade>

            <b-form-group v-if="showContactTemplateSelector">
                <template #label>
                    Fax Template
                    <HelpPopup>
                        Select a template to include insurance plan information (Primary/Secondary Plan details, Copay
                        amount, etc.)
                    </HelpPopup>
                </template>
                <b-select v-model="faxData.prescriberContactTemplateId" :options="contactTemplateOptions" />
            </b-form-group>

            <b-form-group v-if="patientOptions?.length" label="Patient">
                <b-select v-model="faxData.patientId" :options="patientOptions" />
            </b-form-group>

            <b-form-group v-if="prescriptionOptions?.length" label="Prescription">
                <v-select v-model="faxData.rxId" :options="prescriptionOptions" :reduce="option => option.value">
                    <template #option="option">
                        <span v-html="option.html"></span>
                    </template>
                    <template #selected-option="option">
                        <span v-html="option.html"></span>
                    </template>
                </v-select>
            </b-form-group>

            <hr v-if="patientOptions?.length || prescriptionOptions?.length">

            <strong>Send To:</strong>

            <b-select v-model="faxData.sendTo" :options="sendToOptions" />

            <template v-if="faxData.sendTo === 0">
                <SearchComponent :key="faxData.prescriber?.id ?? 0" v-model="faxData.prescriber" :hide-label="true"
                    search-u-r-l="/Prescriber" :clearable="true" @input.native="faxPrescriberTouched = true" />

                <div v-if="faxData.prescriber?.id" class="d-flex align-items-center">
                    <div class="flex-fill">Fax Number: {{ faxData.prescriber.faxNumber }}</div>
                    <div>
                        <b-button size="sm" class="m-0" variant="info" @click="selectedPrescriber = faxData.prescriber">
                            Edit Prescriber
                        </b-button>
                    </div>
                </div>
                <b-check v-if="faxData.prescriber?.id && faxData.prescriber?.altFaxNumber" v-model="useAltFax" switch class="mt-2">
                    Use Alternate Fax Number ({{ faxData.prescriber.altFaxNumber }})
                </b-check>
            </template>

            <b-input v-if="faxData.sendTo === 1" v-model="faxData.faxNumber" placeholder="Enter Fax Number" />

            <div v-if="faxData.sendTo === 3 && activeTask?.storedPharmacy" class="mt-2">
                <div><strong>{{ activeTask.storedPharmacy.name }}</strong></div>
                <div>Fax Number: {{ activeTask.storedPharmacy.faxNumber }}</div>
            </div>

            <template v-if="!isContactTemplateSelected">
                <hr>

                <div class="d-flex flex-column" style="gap: 8px;">
                    <b-check v-if="faxData.patientId || faxData.rxId" v-model="faxData.includePatient" switch>
                        Include Patient Information
                    </b-check>
                    <b-check v-if="faxData.rxId" v-model="faxData.includeRx" switch>
                        Include Prescription Information
                    </b-check>
                    <b-check v-if="activeTask.completionAnswers?.length" v-model="faxData.includeAnswers" switch>
                        Include Completion Answers
                    </b-check>
                    <b-check v-model="faxData.includeNotes" switch>
                        Include Task Notes
                    </b-check>
                </div>
            </template>

            <hr>

            <b-form-group label="Additional Notes to Include">
                <b-textarea rows="3" v-model="faxData.note" />
            </b-form-group>

            <hr v-if="showMergeToggle && !isContactTemplateSelected">

            <template v-if="showMergeToggle && !isContactTemplateSelected">
                <b-form-group label="Merge Document Template (optional)" class="mt-3">
                    <SearchComponent v-model="faxData.mergeDocument"
                        :hide-label="true"
                        search-u-r-l="/TaskCompletionAnswersMergeDocument"
                        :params="{ active: true }"
                        :clearable="true"
                        :option-label="mergeDocumentLabel"
                        prepopulate />
                </b-form-group>

                <template v-if="faxData.mergeDocument">
                    <b-check v-model="includeTaskSummary" switch class="mb-2">
                        Include Task Summary
                    </b-check>
                    <b-check v-model="faxData.linkMergedToPatient" switch>
                        Link Document to Patient Documents
                    </b-check>
                </template>
            </template>

            <template #modal-footer="{ cancel }">
                <b-button @click="cancel" :disabled="sendingFax">
                    Cancel
                </b-button>
                <b-button variant="primary" @click="sendFax" :disabled="sendingFax">
                    <b-spinner v-if="sendingFax" small class="mr-1" />
                    <i v-else class="fa fa-fax mr-1" />
                    {{ sendingFax ? 'Sending Fax...' : 'Send Fax' }}
                </b-button>
            </template>

        </b-modal>

        <b-modal ref="textModal" title="Send Text Message" no-fade>
            <b-alert :show="!!textUnavailableReason" variant="warning" class="p-2">
                {{ textUnavailableReason }}
            </b-alert>

            <b-form-group v-if="patientOptions?.length" label="Patient">
                <b-select v-model="textData.patientId" :options="patientOptions" @change="updateTextPreview" />
            </b-form-group>

            <b-form-group v-if="storeOptions?.length" label="Store">
                <b-select v-model="textData.storeId" :options="storeOptions" @change="updateTextPreview" />
            </b-form-group>

            <b-form-group v-if="textPrescriptionOptions?.length" label="Prescription (optional)">
                <v-select v-model="textData.rxId" :options="textPrescriptionOptions" :reduce="option => option.value"
                    @input="updateTextPreview" clearable>
                    <template #option="option">
                        <span v-html="option.html"></span>
                    </template>
                    <template #selected-option="option">
                        <span v-html="option.html"></span>
                    </template>
                </v-select>
            </b-form-group>

            <b-form-group label="Template">
                <SearchComponent v-model="textData.template" :hide-label="true"
                    search-u-r-l="/CustomTextMessage/available" :clearable="true"
                    :option-label="textTemplateLabel" :results-modifier="filterTextTemplatesByProgram"
                    prepopulate
                    @input="updateTextPreview" />
            </b-form-group>

            <b-form-group v-if="textMessagePreview" label="Preview">
                <b-alert v-if="textPreviewFailed" show variant="danger" class="p-2 mb-0">
                    {{ textMessagePreview }}
                </b-alert>
                <div v-else class="msg-bubble">{{ textMessagePreview }}</div>
            </b-form-group>

            <template #modal-footer="{ cancel }">
                <b-button @click="cancel" :disabled="sendingText">
                    Cancel
                </b-button>
                <b-button variant="primary" @click="sendText" :disabled="sendingText || !textData.template || !!textUnavailableReason">
                    <b-spinner v-if="sendingText" small class="mr-1" />
                    <i v-else class="fa fa-comment mr-1" />
                    {{ sendingText ? 'Sending Text...' : 'Send Text' }}
                </b-button>
            </template>
        </b-modal>

        <b-modal :visible="!!selectedPrescriber" title="Edit Prescriber" size="xl" no-fade hide-footer
            @hidden="selectedPrescriber = null">
            <DoctorFormComponent v-if="selectedPrescriber" :prescriber-id="selectedPrescriber.id" :embedded="true"
                :button-titles="{ new: '', clear: '', save: 'Save Prescriber', cancel: 'Cancel' }"
                @close="selectedPrescriber = null" @saved="faxData.prescriber = $event; selectedPrescriber = null;" />
        </b-modal>
    </div>
</template>

<script lang="ts">
import { Component, Mixins, Ref, Prop, Watch, Emit } from "vue-property-decorator";
import TaskEditor from "@/components/Tasks/TaskEditor.vue";
import { UserTask, UserTaskAnswer, UserTaskQuestionType, UserTaskStatus } from "@/models/UserTask";
import { TaskCompletionAnswersMergeDocument } from "@/models/TaskCompletionAnswersMergeDocument";
import { PrescriberContactTemplate } from "@/models/FaxTemplates";
import { CustomTextMessageTemplate } from "@/models/CustomTextMessageTemplate";
import { Store } from "@/models/Store";
import { BModal, BvModalEvent } from "bootstrap-vue";
import { NotificationOptions } from "@/util/NotificationOptionsPresets";
import { Patient } from "@/models/Patient";
import { PatientDocument } from "@/models/PatientDocument";
import { Prescription } from "@/models/Prescription";
import moment from "moment";
import TaskQuestions from "./TaskQuestions.vue";
import SearchComponent from "../SearchComponent.vue";
import { Prescriber } from "@/models/Prescriber";
import DoctorFormComponent from "../Doctor/DoctorFormComponent.vue";
import Axios, { AxiosError } from "axios";
import ImageComponent from "../Prescription/ImageComponent.vue";
import TextUtils from "@/util/TextUtils";
import PhoneFormatter from "@/util/PhoneFormatter";
import { getUserSetting, saveUserSetting } from "@/services/UserSettingsService";
import { LockHandler } from "@/mixins/LockHandler";
import { EntityType } from "@/models/EntityType";
import HistoryModal from "@/components/Modals/HistoryModal.vue";
import HelpPopup from "../HelpPopup.vue";

const FaxIncludeTaskSummaryKey = "fax-task-include-summary";
const FaxLinkToPatientDocsKey = "fax-task-link-to-patient-docs";

@Component({
    components: {
        TaskEditor,
        TaskQuestions,
        SearchComponent,
        DoctorFormComponent,
        ImageComponent,
        HistoryModal,
        HelpPopup
    },
})
export default class TaskDetails extends Mixins(LockHandler) {

    @Prop({ default: 0 }) protected id!: number | string;
    @Prop() protected patientId?: number | string;
    @Prop() protected prescriptionId?: string;
    @Prop({ default: false }) protected embed!: boolean;
    @Prop({ default: false }) protected scheduled!: boolean;
    @Prop({ default: false }) protected template!: boolean;
    @Prop({ default: () => [] }) protected patientDocuments!: PatientDocument[];

    @Ref("taskModal") protected taskModal?: BModal;
    @Ref("faxModal") protected faxModal?: BModal;
    @Ref("textModal") protected textModal?: BModal;
    @Ref("closureQuestionsModal") protected closureQuestionsModal?: BModal;
    @Ref("cancelTaskModal") protected cancelTaskModal?: BModal;
    @Ref("reopenTaskModal") protected reopenTaskModal?: BModal;
    @Ref("taskEditor") protected taskEditor?: TaskEditor;
    @Ref("closureQuestions") protected closureQuestions?: TaskQuestions;

    protected loading = false;

    private activeTask: UserTask = new UserTask();
    private initialTaskStatus?: number;

    private isCompleting = false;
    private showCompletionQuestionErrors = false;
    private showCompletionQuestionAnswerErrors = false;
    private activeClosureAnswers: UserTaskAnswer[] = [];
    private activeMergeDocuments: TaskCompletionAnswersMergeDocument[] = [];
    private lockedByOtherUser = false;

    // SD-3303 cancel/reopen modal state
    private cancelTaskReason = "";
    private cancelTaskReasonRequired = false;
    private reopenTaskTargetStatus: number = 0;

    UserTaskStatus = UserTaskStatus;

    mounted() {
        this.getTask();
        this.acquireTaskLock();
        this.loadActiveMergeDocuments();
        this.taskModal?.show();

        if (!this.id || this.id === "0") {
            if (this.patientId) {
                this.addDefaultPatient(this.patientId);
            }

            const queryPatientId = this.$route.query.patientid || this.$route.query.patientId;
            if (queryPatientId) {
                this.addDefaultPatient(queryPatientId as string);
            }

            if (this.prescriptionId) {
                this.addDefaultPrescription(this.prescriptionId);
            }

            const queryPrescriptionId = this.$route.query.prescriptionid || this.$route.query.prescriptionId;
            if (queryPrescriptionId) {
                this.addDefaultPrescription(queryPrescriptionId as string);
            }
        }
    }

    @Watch("id")
    onTaskIdChange() {
        this.getTask();
        this.acquireTaskLock();
    }

    async onModalHidden() {
        // Release the lock before signaling 'closed': the Tasks list refreshes on 'closed', and
        // re-fetching before the release POST lands re-paints a stale "locked" padlock for a task
        // that is actually free.
        await this.clearAllLocks();
        this.$emit("closed");
    }

    async acquireTaskLock() {
        const taskId = Number(this.id);
        if (!taskId) {
            await this.clearAllLocks();
            return;
        }

        try {
            await this.setExclusiveLock(`/task/${taskId}/lock`, 60000);
            this.lockedByOtherUser = false;
        } catch (err) {
            const error = err as AxiosError;
            if (error.response?.status === 418) {
                this.lockedByOtherUser = true;
                this.showLockedMessage(
                    EntityType.UserTask,
                    taskId.toString(),
                    this.activeTask?.title ?? taskId.toString(),
                    error.response.data,
                );
            } else {
                this.$notification(NotificationOptions.error(err));
            }
        }
    }

    @Watch("activeTask.status")
    handleStatusChange(newStatus: number) {
        this.isCompleting = (newStatus === 2);
    }

    async addDefaultPatient(patientId: string | number) {
        try {
            const response = await this.$http.get<Patient>(`/Patient/${patientId}`);
            this.taskEditor?.addPatient(Object.assign(new Patient(), response.data));
        } catch (err) {
            console.error(err);
            this.$notification(NotificationOptions.error(err));
        }
    }

    async addDefaultPrescription(prescriptionId: string) {
        try {
            const prescriptionResponse = await this.$http.get<Prescription>(`/Prescription/${prescriptionId}`);
            const prescription = Object.assign(new Prescription(), prescriptionResponse.data);

            const prescriberResponse = await this.$http.get<Prescriber>(`/Prescriber/${prescription.prescriberID}`);
            prescription.prescriber = prescriberResponse.data;

            this.taskEditor?.addPrescription(prescription);
        } catch (err) {
            console.error(err);
            this.$notification(NotificationOptions.error(err));
        }
    }

    @Emit()
    async getTask() {
        if (!Number(this.id)) {
            this.activeTask = Object.assign(new UserTask(), {
                isScheduled: this.scheduled,
                isTemplate: this.template,
                startDate: moment.utc([moment().year(), moment().month(), moment().date()]),
                dueDate: moment.utc([moment().year(), moment().month(), moment().date()]),
                expiredOnDate: null,
                enabled: true,
                repeatMode: 0,
                completionQuestionSet: {
                    questions: []
                },
                completionAnswers: [],
                limitToTeamIds: [],
                limitToProgramIds: [],
                limitToRxStatuses: [],
                blockCheckout: false,
                patientDocuments: this.patientDocuments.length ? this.patientDocuments : []
            });
            this.initialTaskStatus = this.activeTask.status; // Set for new tasks too
            return;
        }

        this.loading = true;

        try {
            const response = await this.$http.get(`/task/${this.id}`);
            this.activeTask = new UserTask(response.data);
            this.initialTaskStatus = response.data.status;
            this.activeClosureAnswers = this.activeTask.completionAnswers ?? [];
            return this.activeTask;
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
            console.error(err);
        } finally {
            this.loading = false;
        }
    }

    validateTask() {
        this.showCompletionQuestionErrors = false;

        if (!this.activeTask.title?.trim()) {
            this.$notification(NotificationOptions.error("You must provide a title for this task."));
            return false;
        }

        if (!this.activeTask.startDate) {
            this.$notification(NotificationOptions.error("You must provide a start date for this task."));
            return false;
        }

        if (!this.activeTask.dueDate) {
            this.$notification(NotificationOptions.error("You must provide a due date for this task."));
            return false;
        }

        if (this.taskEditor?.requireTaskAssignment && (!this.activeTask.assignments || this.activeTask.assignments.length === 0)) {
            this.$notification(NotificationOptions.error("A task assignment is required. Please assign this task before saving."));
            return false;
        }

        const isScheduled = this.activeTask.isScheduled;
        const repeatSchedule = this.activeTask.repeatSchedule;

        if (isScheduled && !repeatSchedule) {
            this.$notification(NotificationOptions.error("You must select a schedule for this scheduled task."));
            return false;
        }

        if (isScheduled && this.activeTask.repeatMode === 0 && (repeatSchedule === "0" || repeatSchedule?.includes("-"))) {
            this.$notification(NotificationOptions.error("You must enter a number of days greater than 0."));
            return false;
        }

        const isTemplate = this.activeTask.isTemplate;
        const hasCompletionQuestions = !!this.activeTask.completionQuestionSet?.questions?.length;
        const canEditCompletionQuestions = isScheduled || isTemplate || !this.activeTask.id;

        if (canEditCompletionQuestions && hasCompletionQuestions) {
            const questionsHaveErrors = this.activeTask.completionQuestionSet?.questions.some(q => {
                if (!q.text?.trim()) return true;
                if (![UserTaskQuestionType.multipleChoiceMultiple, UserTaskQuestionType.multipleChoiceSingle].includes(q.typeId)) return false;
                if (q.options.some(o => !o.text?.trim())) return true;
                const uniqueTexts = new Set(q.options.map(o => o.text?.trim()));
                if (uniqueTexts.size !== q.options.length) return true;
                return false;
            });
            if (questionsHaveErrors) {
                this.$nextTick(() => {
                    this.showCompletionQuestionErrors = true;
                });
                this.$notification(NotificationOptions.error("Some completion questions have errors."));
                return false;
            }
        }

        return true;
    }

    protected loadingCompletionForm = false;
    protected processingPreSave = false;
    protected savingPartialAnswers = false;

    async handleSaveTask(e?: any) {
        e?.preventDefault();

        if (!this.validateTask()) {
            return;
        }

        // Save any pending note content before saving the task
        await this.taskEditor?.savePendingNote();

        if (!this.isCompleting) {
            await this.saveTask();
            return;
        }

        // Templates and scheduled tasks should never show completion modal
        if (this.activeTask.isTemplate || this.activeTask.isScheduled) {
            await this.saveTask();
            return;
        }

        try {
            this.processingPreSave = true;

            const hasClosureQuestions = !!this.activeTask.completionQuestionSet?.questions?.length;
            const hasCompletionForm = !!this.activeTask.taskCompletionFormId;

            let shouldShowCompletionModal = hasClosureQuestions;

            if (hasCompletionForm) {
                this.activeTask.completionFormImageId = null;
                this.activeTask.completionFormImageAccepted = null;

                await this.generateCompletionForm();

                if (this.activeTask.completionFormImageId) {
                    shouldShowCompletionModal = true;
                }
            }

            if (shouldShowCompletionModal) {
                this.closureQuestionsModal?.show();
                this.taskEditor?.resetActiveTab();

                return;
            }

            await this.saveTask();
        } catch (err) {
            console.warn(err);
        } finally {
            this.processingPreSave = false;
        }
    }

    async generateCompletionForm() {
        try {
            this.loadingCompletionForm = true;
            const response = await Axios.post<number | null>(`/task/generate-completion-form/${this.activeTask.id}`);
            this.activeTask.completionFormImageId = !response.data ? null : response.data;
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        } finally {
            this.loadingCompletionForm = false;
        }
    }

    async saveTask() {
        try {
            // Clean up empty taskCompletionForm object
            if (this.activeTask.taskCompletionForm && !this.activeTask.taskCompletionForm.id) {
                this.activeTask.taskCompletionForm = null;
            }

            const response = await this.$http.post<UserTask>("/task/", this.activeTask);
            this.activeTask = new UserTask(response.data);
            this.$emit("saved", response.data);
            this.$notification(NotificationOptions.success(response.data.isScheduled ? "Scheduled Task saved" : response.data.isTemplate ? "Template saved" : "Task saved"));
        } catch (err) {
            const error = err as AxiosError;
            if (error.response?.status === 418 && this.activeTask?.id) {
                this.showLockedMessage(
                    EntityType.UserTask,
                    this.activeTask.id.toString(),
                    this.activeTask.title ?? this.activeTask.id.toString(),
                    error.response.data,
                );
                return;
            }
            this.$notification(NotificationOptions.error(err));
            console.error(err);
        }
    }

    async updateWatchers() {
        if (!this.activeTask.id) return;
        try {
            const response = await this.$http.post<UserTask>("/task/watch", this.activeTask);
            this.$emit("updated", response.data);
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
            console.error(err);
        }
    }

    async completeActiveTask() {
        this.activeTask.status = UserTaskStatus.complete;
        this.$nextTick(() => {
            this.handleSaveTask();
        })
    }

    // SD-3303: open the cancel-task modal. Pre-resolves whether a reason is required so the
    // OK button can stay disabled until the user types one. The check mirrors the BE rule
    // (template override wins; otherwise fall back to global Configuration.RequireTaskCancellationReason).
    async openCancelTaskModal() {
        try {
            this.cancelTaskReason = "";
            let templateOverride: boolean | null = null;
            if (this.activeTask?.createdByTemplateId) {
                try {
                    const tmplResp = await this.$http.get<UserTask>(`/task/${this.activeTask.createdByTemplateId}`);
                    templateOverride = tmplResp.data.requireCancellationReasonOverride ?? null;
                } catch {
                    templateOverride = null;
                }
            }
            if (templateOverride !== null) {
                this.cancelTaskReasonRequired = templateOverride;
            } else {
                const cfg = await this.$http.get<boolean>("/api/Configuration/RequireTaskCancellationReason");
                this.cancelTaskReasonRequired = !!cfg.data;
            }
        } catch {
            this.cancelTaskReasonRequired = false;
        }
        this.cancelTaskModal?.show();
    }

    async confirmCancelTask(bvEvent: any) {
        // BootstrapVue does not await @ok handlers, so the modal would close before the
        // POST completes. Prevent the default close synchronously and re-hide manually only
        // on success — matches the pattern in handleCompleteQuestionsSubmit.
        bvEvent.preventDefault();

        if (this.cancelTaskReasonRequired && !this.cancelTaskReason.trim()) {
            return;
        }
        try {
            const resp = await this.$http.post<UserTask>(
                `/task/${this.activeTask?.id}/cancel`,
                { reason: this.cancelTaskReason.trim() || null });
            this.$notification(NotificationOptions.success("Task cancelled."));
            this.$emit("updated", resp.data);
            this.cancelTaskModal?.hide();
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        }
    }

    openReopenTaskModal() {
        this.reopenTaskTargetStatus = UserTaskStatus.new;
        this.reopenTaskModal?.show();
    }

    async confirmReopenTask(bvEvent: any) {
        // See confirmCancelTask — BootstrapVue does not await @ok handlers.
        bvEvent.preventDefault();

        try {
            const resp = await this.$http.post<UserTask>(
                `/task/${this.activeTask?.id}/reopen`,
                { targetStatus: this.reopenTaskTargetStatus });
            this.$notification(NotificationOptions.success("Task reopened."));
            this.$emit("updated", resp.data);
            this.reopenTaskModal?.hide();
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        }
    }

    get getTitle() {
        if (this.loading || !this.activeTask) return "";

        if (this.activeTask.isScheduled) {
            return this.activeTask?.id ? 'Edit Scheduled Task' : 'New Scheduled Task';
        }

        if (this.activeTask.isTemplate) {
            return this.activeTask?.id ? 'Edit Task Template' : 'New Task Template';
        }

        return this.activeTask.id ? 'Edit Task' : 'New Task';
    }

    get showCompleteAction() {
        return !this.activeTask?.isScheduled &&
            !this.activeTask?.isTemplate &&
            this.activeTask?.id &&
            this.activeTask?.status !== UserTaskStatus.complete &&
            this.activeTask?.status !== UserTaskStatus.cancelled &&
            !this.lockedByOtherUser;
    }

    get hasCancelTasksPermission(): boolean {
        return this.$permissions?.some(p => p.permissionCodeName === "Admin/Cancel Tasks") ?? false;
    }

    get showCancelTaskAction(): boolean {
        return !!this.activeTask?.id
            && !this.activeTask?.isScheduled
            && !this.activeTask?.isTemplate
            && (this.activeTask?.status === UserTaskStatus.new || this.activeTask?.status === UserTaskStatus.inProgress)
            && !this.lockedByOtherUser
            && this.hasCancelTasksPermission;
    }

    get showReopenTaskAction(): boolean {
        return !!this.activeTask?.id
            && !this.activeTask?.isScheduled
            && !this.activeTask?.isTemplate
            && this.activeTask?.status === UserTaskStatus.cancelled
            && this.hasCancelTasksPermission;
    }

    get showMergeAnswersAction() {
        return !this.activeTask?.isScheduled &&
            !this.activeTask?.isTemplate &&
            this.activeTask?.id &&
            this.activeTask?.status === UserTaskStatus.complete &&
            this.activeTask?.taskCompletionQuestionSetId &&
            this.activeMergeDocuments.length > 0 &&
            (this.$permissions?.some(p => p.permissionCodeName === "Admin/Task Completion Answers Generate") ?? false);
    }

    get showWatching() {
        return !this.activeTask?.isScheduled &&
            !this.activeTask?.isTemplate &&
            this.activeTask?.watchers?.find(w => w.userName?.toLocaleLowerCase() === this.$user.userName.toLowerCase()) &&
            (this.initialTaskStatus !== UserTaskStatus.complete || this.hasModifyCompletedTasksPermission);
    }

    get showWatchAction() {
        return !this.activeTask?.isScheduled &&
            !this.activeTask?.isTemplate &&
            !this.activeTask?.watchers?.find(w => w.userName?.toLocaleLowerCase() === this.$user.userName.toLowerCase()) &&
            (this.initialTaskStatus !== UserTaskStatus.complete || this.hasModifyCompletedTasksPermission);
    }

    get hasModifyCompletedTasksPermission() {
        return this.$permissions?.some(p => p.permissionCodeName === "Processing/Modify Completed Tasks") ?? false;
    }

    get shouldDisableFields() {
        // If we're still loading or haven't set initial status yet, don't disable
        if (this.loading || this.initialTaskStatus === undefined) {
            return false;
        }

        if (this.lockedByOtherUser) return true;

        return this.initialTaskStatus === UserTaskStatus.complete &&
               !this.hasModifyCompletedTasksPermission;
    }

    get showSaveButton() {
        if (this.lockedByOtherUser) return false;
        return this.initialTaskStatus !== UserTaskStatus.complete || this.hasModifyCompletedTasksPermission;
    }

    addMeAsWatcher() {
        this.taskEditor?.addMeAsWatcher();
        this.$nextTick(() => {
            this.updateWatchers();
        });
    }

    processQuestionAnswer() {
        this.activeTask.completionAnswers = this.closureQuestions?.cleanAnswers();
    }

    async handleCompleteQuestionsSubmit(e: any) {
        e.preventDefault();

        this.showCompletionQuestionAnswerErrors = false;

        if (this.closureQuestions?.answersHaveErrors()) {
            this.$nextTick(() => {
                this.showCompletionQuestionAnswerErrors = true;
            });

            this.$notification(NotificationOptions.error("There are errors on the completion questions."));
            return;
        }

        if (this.activeTask.completionFormImageId && this.activeTask.completionFormImageAccepted === null) {
            this.$notification(NotificationOptions.error("You must accept or reject the form."));
            return;
        }

        await this.saveTask();
        this.closureQuestionsModal?.hide();
    }

    async handleSavePartialAnswers() {
        try {
            this.savingPartialAnswers = true;

            this.processQuestionAnswer();

            this.activeTask.status = this.initialTaskStatus ?? UserTaskStatus.inProgress;

            if (this.activeTask.taskCompletionForm && !this.activeTask.taskCompletionForm.id) {
                this.activeTask.taskCompletionForm = undefined;
            }

            // Save the task
            const response = await this.$http.post<UserTask>("/task/", this.activeTask);
            this.activeTask = new UserTask(response.data);
            this.$emit("partial-saved", response.data);

            this.closureQuestionsModal?.hide();

            this.$notification(NotificationOptions.success("Partial answers saved."));
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
            console.error(err);
        } finally {
            this.savingPartialAnswers = false;
        }
    }

    protected sendingFax = false;
    protected useAltFax = false;

    protected faxData = {
        patientId: 0,
        rxId: "",
        prescriber: new Prescriber(),
        includePatient: true,
        includeRx: true,
        includeNotes: false,
        includeAnswers: true,
        note: "",
        sendTo: 0,
        faxNumber: "",
        mergeDocument: null,
        mergeMode: "attach",
        linkMergedToPatient: true,
        // null = the default Task Summary; a template id = generate from that Prescriber Contact template.
        prescriberContactTemplateId: null as number | null
    };

    // Prescriber Contact templates scoped to this task's program; only loaded/offered for program-linked tasks.
    protected contactTemplates: PrescriberContactTemplate[] = [];

    protected textData = {
        patientId: 0,
        rxId: null as string | null,
        storeId: 0,
        template: null as any
    };

    protected textMessagePreview = "";
    // The preview renders in an outbound-message bubble, so a failure must not be styled as the message.
    protected textPreviewFailed = false;
    protected sendingText = false;
    // Must not start with an underscore, or Vue leaves it unproxied and every
    // comparison against it comes out false. See faxPrescriberSeq.
    private previewSeq = 0;

    // Latest-wins guard for the async ingestion prescriber seed; bumped on every modal open
    // so an in-flight lookup from a previous open can never land on the current one.
    // Must not start with an underscore: Vue treats such keys as reserved and never proxies
    // them onto the instance, so `this._name` reads undefined and ++ yields a NaN that
    // fails every subsequent equality check.
    private faxPrescriberSeq = 0;

    // Set once the user types into the prescriber search, so a late seed can't remount the
    // SearchComponent (via its :key) and throw away a query they are part-way through.
    protected faxPrescriberTouched = false;

    // Tasks reference stores by ID only; names are resolved from this list for the store dropdown.
    protected stores: Store[] = [];

    showFaxModal() {
        const seedSeq = ++this.faxPrescriberSeq;
        this.faxPrescriberTouched = false;

        this.faxData.rxId = "";
        this.faxData.patientId = 0;
        this.faxData.prescriber = new Prescriber();
        this.faxData.mergeDocument = null;
        this.faxData.mergeMode = "attach";
        this.faxData.linkMergedToPatient = true;
        this.faxData.prescriberContactTemplateId = null;
        this.useAltFax = false;

        // Prescriber Contact templates are only relevant for program-linked (e.g. adherence) tasks.
        if (this.activeTask?.programId) {
            this.loadContactTemplates();
        } else {
            this.contactTemplates = [];
        }

        const savedIncludeSummary = getUserSetting(FaxIncludeTaskSummaryKey);
        if (savedIncludeSummary !== null) {
            this.faxData.mergeMode = JSON.parse(savedIncludeSummary) ? "attach" : "replace";
        }

        const savedLinkToPatient = getUserSetting(FaxLinkToPatientDocsKey);
        if (savedLinkToPatient !== null) {
            this.faxData.linkMergedToPatient = JSON.parse(savedLinkToPatient);
        }

        if (this.patientOptions?.length) {
            this.faxData.patientId = this.patientOptions[0].value;
        }

        if (this.prescriptionOptions?.length) {
            this.faxData.rxId = this.prescriptionOptions[0].value;
        } else if (!this.activeTask?.prescriptions?.length) {
            // Gate on the task's prescriptions, not the patient-filtered prescriptionOptions:
            // where a prescription exists it — not ingestion — is the authoritative prescriber.
            // If the patient filter hides them all, the field stays blank until the user picks a
            // prescription and faxDataRxChanged() seeds it, rather than guessing from ingestion.
            this.seedFaxPrescriberFromIngestion(seedSeq);
        }

        //this.faxDataRxChanged();

        this.faxModal?.show();
    }

    /**
     * Ingested tasks carry prescriber demographics but no linked prescription, so the
     * prescription-driven seed in faxDataRxChanged() never fires. Resolve by NPI instead.
     * A missing NPI, no match, or records that disagree on the fax number leaves the field
     * blank and manually searchable — never an error, never a guess.
     */
    private async seedFaxPrescriberFromIngestion(seedSeq: number) {
        const npi = this.activeTask?.ingestionData?.prescriberNPI?.trim();
        if (!npi) return;

        // Pin the task this NPI came from: the id prop can change while the modal stays open,
        // swapping activeTask underneath us without reopening it or bumping the sequence.
        const seedTaskId = this.activeTask?.id;

        try {
            // The endpoint prefix-matches NPI, so narrow the results to an exact hit ourselves.
            const response = await this.$http.get<Prescriber[]>("/Prescriber", { params: { searchTerm: npi } });
            const matches = (response.data ?? []).filter(p => p.npiNumber?.trim() === npi);
            const prescriber = this.unambiguousFaxTarget(matches);
            if (!prescriber) return;

            // A reopen, task swap, manual pick, or an in-progress search while the lookup was in
            // flight would seed the wrong prescriber and fax PHI to the wrong provider.
            if (seedSeq !== this.faxPrescriberSeq
                || this.activeTask?.id !== seedTaskId
                || this.faxData.prescriber?.id
                || this.faxPrescriberTouched) return;

            this.faxData.prescriber = new Prescriber(prescriber);
        } catch (error) {
            console.error("Error resolving fax prescriber from ingestion data:", error);
        }
    }

    /**
     * One NPI routinely spans several prescriber records — one per practice address, until
     * someone merges them — so demanding a single row would skip the common case. Duplicates
     * are interchangeable for faxing only when they agree on a real fax destination; the
     * chosen record still supplies the alternate fax number, which the user can see and toggle.
     */
    private unambiguousFaxTarget(matches: Prescriber[]): Prescriber | null {
        if (!matches.length) return null;
        if (matches.length === 1) return matches[0];

        const faxNumbers = new Set(matches.map(p => PhoneFormatter.formatForTwilio(p.faxNumber)));
        if (faxNumbers.size !== 1 || faxNumbers.has("")) return null;

        return matches[0];
    }

    mergeDocumentLabel(doc: TaskCompletionAnswersMergeDocument) {
        return doc?.name || "";
    }

    showMergeModal() {
        // Emit event to parent to show merge document selection modal
        this.$emit('show-merge-modal', {
            taskId: this.activeTask.id,
            documents: this.activeMergeDocuments
        });
    }

    async loadActiveMergeDocuments() {
        try {
            const response = await this.$http.get('/Task/active-merge-documents');
            this.activeMergeDocuments = response.data;
        } catch (error) {
            console.error('Error loading active merge documents:', error);
            // Fail silently to not interrupt task functionality
        }
    }

    async loadContactTemplates() {
        try {
            const response = await this.$http.get<PrescriberContactTemplate[]>("/Prescriber/contact-templates", {
                params: { onlyActive: true }
            });
            this.contactTemplates = response.data ?? [];
        } catch (error) {
            console.error("Error loading prescriber contact templates:", error);
            // Fail silently — the task fax falls back to the Task Summary template.
            this.contactTemplates = [];
        }
    }

    // Templates scoped to no program (global) or to this task's program, mirroring the prescription dialog.
    get availableContactTemplates() {
        return this.contactTemplates.filter(t =>
            !t.programs?.length || t.programs.some(p => p.id === this.activeTask?.programId));
    }

    // Offer the template chooser only for program-linked tasks that actually have a usable template.
    // showFaxModal only fetches the templates for program-linked tasks, so this mirrors that gate.
    get showContactTemplateSelector() {
        return !!this.activeTask?.programId && this.availableContactTemplates.length > 0;
    }

    get contactTemplateOptions() {
        return [
            { value: null, text: "Task Summary" },
            ...this.availableContactTemplates.map(t => ({ value: t.id, text: t.name }))
        ];
    }

    get isContactTemplateSelected() {
        return this.faxData.prescriberContactTemplateId != null;
    }

    async sendFax(e: BvModalEvent) {
        e.preventDefault();

        // Prevent double-submission if already sending
        if (this.sendingFax) return;

        try {
            this.sendingFax = true;

            const data = {
                ...this.faxData,
                taskId: this.activeTask.id,
                mergeDocumentId: this.faxData.mergeDocument?.id ?? null,
                mergeMode: this.faxData.mergeDocument ? this.faxData.mergeMode : null,
                linkMergedToPatient: this.faxData.mergeDocument ? this.faxData.linkMergedToPatient : false,
                faxNumber: this.faxData.faxNumber ? TextUtils.formatFaxNumber(this.faxData.faxNumber) : this.faxData.faxNumber,
                useAltFax: this.useAltFax
                // prescriberContactTemplateId comes from the Fax Template selector via the spread above.
            };
            var resp = await this.$http.post("/task/fax", data);

            this.$notification(NotificationOptions.success(resp.data));
            this.faxModal?.hide();
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        } finally {
            this.sendingFax = false;
        }
    }

    async loadStores() {
        if (this.stores.length) return;

        try {
            const response = await this.$http.get<Store[]>("/Store/");
            this.stores = response.data ?? [];
        } catch (error) {
            console.error("Error loading stores:", error);
            // Fail silently — store options fall back to their ID-only label.
            this.stores = [];
        }
    }

    // Templates scoped to no program (global) or to this task's program, mirroring the fax selector.
    // The text model exposes `limitToPrograms`; the fax model's equivalent is `programs`.
    filterTextTemplatesByProgram(templates: CustomTextMessageTemplate[]) {
        return (templates ?? []).filter(t =>
            !t.limitToPrograms?.length || t.limitToPrograms.some(p => p.id === this.activeTask?.programId));
    }

    textTemplateLabel(template: CustomTextMessageTemplate) {
        return template?.name || "";
    }

    showTextModal() {
        this.textData.rxId = null;
        this.textData.patientId = 0;
        this.textData.storeId = 0;
        this.textData.template = null;
        this.textMessagePreview = "";
        this.textPreviewFailed = false;

        this.loadStores();

        if (this.patientOptions?.length) {
            this.textData.patientId = this.patientOptions[0].value;
        }

        if (this.storeOptions?.length) {
            this.textData.storeId = this.storeOptions[0].value;
        }

        if (this.textPrescriptionOptions?.length) {
            this.textData.rxId = this.textPrescriptionOptions[0].value;
        }

        this.textModal?.show();
    }

    async updateTextPreview() {
        // No store gate: the API treats the store as optional and falls back to default branding.
        if (!this.textData.template || !this.textData.patientId) {
            this.textMessagePreview = "";
            this.textPreviewFailed = false;
            return;
        }

        const seq = ++this.previewSeq;

        try {
            const params = {
                storeId: this.textData.storeId,
                templateId: this.textData.template.id,
                patientId: this.textData.patientId,
                rxId: this.textData.rxId || undefined
            };

            const response = await this.$http.get("/CustomTextMessage/preview", { params });
            if (seq === this.previewSeq) {
                this.textMessagePreview = response.data;
                this.textPreviewFailed = false;
            }
        } catch (err) {
            if (seq === this.previewSeq) {
                console.error("Error fetching text preview:", err);
                this.textMessagePreview = "Error loading preview";
                this.textPreviewFailed = true;
            }
        }
    }

    async sendText() {
        if (!this.textData.template) {
            this.$notification(NotificationOptions.error("Please select a template"));
            return;
        }

        this.sendingText = true;

        try {
            const payload = {
                taskId: this.activeTask.id,
                patientId: this.textData.patientId || null,
                rxId: this.textData.rxId || null,
                storeId: this.textData.storeId || null,
                templateId: this.textData.template.id
            };

            await this.$http.post(`/Task/${this.activeTask.id}/text`, payload);
            this.$notification(NotificationOptions.success("Text message sent successfully"));
            this.textModal?.hide();
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        } finally {
            this.sendingText = false;
        }
    }

    get patientOptions() {
        const patients: any[] = [];

        this.activeTask?.patients?.forEach(p => {
            patients.push({ value: p.id, text: `${p.lastName}, ${p.firstName}` });
        })

        return patients;
    }

    get prescriptionOptions() {
        if (this.faxData.patientId) {
            return this.activeTask?.prescriptions?.filter(rx => rx.patientID === this.faxData.patientId)
                .map(rx => new Prescription(rx))
                .map(rx => ({ value: rx.rxID, label: rx.rxID, html: rx.toString() }));
        }
        return this.activeTask?.prescriptions?.map(rx => new Prescription(rx))
            .map(rx => ({ value: rx.rxID, label: rx.rxID, html: rx.toString() }));
    }

    get textPrescriptionOptions() {
        const prescriptions = this.textData.patientId
            ? this.activeTask?.prescriptions?.filter(rx => rx.patientID === this.textData.patientId)
            : this.activeTask?.prescriptions;
        return prescriptions?.map(rx => new Prescription(rx))
            .map(rx => ({ value: rx.rxID, label: rx.rxID, html: rx.toString() }));
    }

    get storeOptions() {
        // Dedupe store IDs from the task's prescriptions plus the task store, preserving first-seen order.
        const storeIds = [
            ...(this.activeTask?.prescriptions?.map(rx => rx.storeID) ?? []),
            this.activeTask?.storeId,
        ].filter((id): id is number => !!id);

        return [...new Set(storeIds)].map(id => ({ value: id, text: this.storeLabel(id) }));
    }

    // Built inline rather than via Store.toString(), whose no-name fallback returns HTML
    // that a b-form-select would render as literal markup.
    private storeLabel(id: number) {
        const name = this.stores.find(s => s.id === id)?.name;
        return name ? `${id} - ${name}` : `Store ${id}`;
    }

    // The patient is the recipient, so the API rejects any send without one. A store is optional.
    // Say so up front rather than leaving the preview blank and the send to fail.
    get textUnavailableReason() {
        if (!this.patientOptions?.length) return "This task has no associated patient, so a text message cannot be sent.";
        return "";
    }

    @Watch("faxData.rxId")
    faxDataRxChanged() {
        const rx = this.activeTask?.prescriptions?.find(rx => new Prescription(rx).rxID === this.faxData.rxId);

        if (!rx) {
            this.faxData.prescriber = new Prescriber();
        } else {
            this.faxData.prescriber = Object.assign(new Prescriber(), { id: rx.prescriberID });
        }
        this.useAltFax = false;
    }

    @Watch("faxData.prescriber")
    faxDataPrescriberChanged() {
        this.useAltFax = false;
    }

    // Selecting a template hides the merge-document control but would otherwise leave its value in the
    // payload, and mergeMode "replace" discards the rendered template entirely. Clear it so a hidden
    // control cannot contribute to the fax.
    @Watch("faxData.prescriberContactTemplateId")
    faxDataContactTemplateChanged() {
        if (this.isContactTemplateSelected) {
            this.faxData.mergeDocument = null;
        }
    }

    get sendToOptions() {
        const options = [{ value: 0, text: 'Prescriber' }, { value: 1, text: 'Other Fax Number' }];

        if (this.activeTask?.storedPharmacy?.faxNumber)
            options.push({ value: 3, text: 'Stored Pharmacy' });

        if (this.patientOptions?.length || this.prescriptionOptions?.length)
            options.push({ value: 2, text: 'No Fax' });

        return options;
    }

    get showMergeToggle() {
        // Only show merge toggle if:
        // 1. Task is completed
        // 2. Task has completion answers
        // 3. User has permission to merge answers
        return this.activeTask?.status === UserTaskStatus.complete &&
            this.activeTask?.completionAnswers?.length > 0 &&
            (this.$permissions?.some(p => p.permissionCodeName === "Admin/Task Completion Answers Generate") ?? false);
    }

    get includeTaskSummary() {
        return this.faxData.mergeMode === "attach";
    }

    set includeTaskSummary(value: boolean) {
        this.faxData.mergeMode = value ? "attach" : "replace";
        saveUserSetting(FaxIncludeTaskSummaryKey, JSON.stringify(value));
    }

    @Watch("faxData.linkMergedToPatient")
    onLinkMergedToPatientChange(value: boolean) {
        saveUserSetting(FaxLinkToPatientDocsKey, JSON.stringify(value));
    }

    protected selectedPrescriber: Prescriber | null = null;
}

</script>

<style scoped lang="scss">
.actions {
    position: sticky;
    bottom: 0;
    padding: 6px;
    z-index: 400;
}

::v-deep(.modal-content .modal-header) {
    flex-shrink: inherit;
    padding-top: 15px;
    padding-bottom: 0;
}

::v-deep(.modal-content .modal-header button) {
    position: inherit;
}

::v-deep(.modal-content .modal-header button:disabled) {
    border-color: transparent;
    opacity: .9;
    padding-left: 0;
    padding-right: 0;
}
</style>
